/**
 * Contest geo + country display helpers (ISO2 codes + flag assets).
 * Seed/leaderboard use these names so flags stay consistent with COUNTRIES.
 */

export interface CountryMeta {
  name: string;
  code: string; // ISO 3166-1 alpha-2
  flagEmoji: string;
}

/** Countries used in contest leaderboard geos (aligned with @utils/countries names). */
export const CONTEST_COUNTRY_META: CountryMeta[] = [
  { name: 'India', code: 'IN', flagEmoji: '🇮🇳' },
  { name: 'United Arab Emirates', code: 'AE', flagEmoji: '🇦🇪' },
  { name: 'Singapore', code: 'SG', flagEmoji: '🇸🇬' },
  { name: 'Malaysia', code: 'MY', flagEmoji: '🇲🇾' },
  { name: 'United Kingdom', code: 'GB', flagEmoji: '🇬🇧' },
  { name: 'United States', code: 'US', flagEmoji: '🇺🇸' },
  { name: 'Australia', code: 'AU', flagEmoji: '🇦🇺' },
  { name: 'Canada', code: 'CA', flagEmoji: '🇨🇦' },
  { name: 'Germany', code: 'DE', flagEmoji: '🇩🇪' },
  { name: 'Japan', code: 'JP', flagEmoji: '🇯🇵' },
  { name: 'South Africa', code: 'ZA', flagEmoji: '🇿🇦' },
  { name: 'Brazil', code: 'BR', flagEmoji: '🇧🇷' },
];

const BY_NAME = new Map(CONTEST_COUNTRY_META.map((c) => [c.name.toLowerCase(), c]));
const BY_CODE = new Map(CONTEST_COUNTRY_META.map((c) => [c.code.toUpperCase(), c]));

export function resolveCountryMeta(
  country?: string | null,
  countryCode?: string | null,
): CountryMeta | null {
  const code = String(countryCode || '').trim().toUpperCase();
  if (code && BY_CODE.has(code)) return BY_CODE.get(code)!;
  const name = String(country || '').trim().toLowerCase();
  if (name && BY_NAME.has(name)) return BY_NAME.get(name)!;
  if (!country && !code) return null;
  return {
    name: country || code || 'Unknown',
    code: code || 'XX',
    flagEmoji: '🏳️',
  };
}

/** Flag image URL (flagcdn) — prefer over emoji for consistent UI sizing. */
export function getCountryFlagUrl(code: string, width: 20 | 40 | 80 = 20): string {
  const cc = String(code || '').trim().toLowerCase();
  if (!cc || cc === 'xx') return '';
  return `https://flagcdn.com/w${width}/${cc}.png`;
}

/** City-level approx coords for leaderboard map (not street GPS). */
export const CONTEST_CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  bengaluru: { lat: 12.9716, lng: 77.5946 },
  chennai: { lat: 13.0827, lng: 80.2707 },
  hyderabad: { lat: 17.385, lng: 78.4867 },
  mumbai: { lat: 19.076, lng: 72.8777 },
  pune: { lat: 18.5204, lng: 73.8567 },
  ahmedabad: { lat: 23.0225, lng: 72.5714 },
  delhi: { lat: 28.7041, lng: 77.1025 },
  jaipur: { lat: 26.9124, lng: 75.7873 },
  chandigarh: { lat: 30.7333, lng: 76.7794 },
  kolkata: { lat: 22.5726, lng: 88.3639 },
  bhubaneswar: { lat: 20.2961, lng: 85.8245 },
  patna: { lat: 25.5941, lng: 85.1376 },
  dubai: { lat: 25.2048, lng: 55.2708 },
  singapore: { lat: 1.3521, lng: 103.8198 },
  'kuala lumpur': { lat: 3.139, lng: 101.6869 },
  london: { lat: 51.5074, lng: -0.1278 },
  'new york': { lat: 40.7128, lng: -74.006 },
  sydney: { lat: -33.8688, lng: 151.2093 },
  toronto: { lat: 43.6532, lng: -79.3832 },
  berlin: { lat: 52.52, lng: 13.405 },
  tokyo: { lat: 35.6762, lng: 139.6503 },
  'cape town': { lat: -33.9249, lng: 18.4241 },
  'são paulo': { lat: -23.5505, lng: -46.6333 },
  'sao paulo': { lat: -23.5505, lng: -46.6333 },
};

export function resolveCityCoords(
  city?: string | null,
  latitude?: number | null,
  longitude?: number | null,
): { lat: number; lng: number } | null {
  if (
    latitude != null &&
    longitude != null &&
    Number.isFinite(Number(latitude)) &&
    Number.isFinite(Number(longitude))
  ) {
    return { lat: Number(latitude), lng: Number(longitude) };
  }
  const key = String(city || '')
    .trim()
    .toLowerCase();
  if (!key) return null;
  return CONTEST_CITY_COORDS[key] || null;
}

/** Equirectangular projection → percent of map box. */
export function latLngToMapPercent(lat: number, lng: number): { left: number; top: number } {
  return {
    left: ((lng + 180) / 360) * 100,
    top: ((90 - lat) / 180) * 100,
  };
}

/** Contest score (0–100 avg) → grade for map marker coloring. */
export function contestScoreToGrade(score: number): string {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'C';
  if (n >= 90) return 'A+';
  if (n >= 80) return 'A';
  if (n >= 70) return 'B';
  if (n >= 60) return 'C';
  return 'D';
}
